import styled from '@emotion/styled';
import {Container, media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';
import Link from 'next/link';

import {PayloadImage} from 'UI/components/layout/PayloadImage';

import {Menu} from 'UI/modules/LachenschmidLayout/Menu';
import {TopBar} from 'UI/modules/LachenschmidLayout/TopBar';

import {useHeader} from './useHeader';

import type {Header as HeaderType} from 'types/payload-types';

/**
 * The props for the `Header` component, which renders the website’s top-level header including contact links,
 * logo, and primary navigation.
 * This interface defines the structure expected by the `Header` component. It includes the `headerData` object
 * which supplies configuration for email, phone, navigation, and branding. The optional `testId` enables precise
 * targeting of the component in end-to-end testing environments by applying it as a `data-cy` attribute.
 */
interface ComponentProps {
    /**
     * The configuration and content data required to render the header. This includes navigation items,
     * logo image, and organizational contact details.
     */
    headerData: HeaderType;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Header'
     */
    testId?: string;
}

/**
 * A layout component that renders the main site header with top bar contact links and primary navigation menu.
 * This component leverages the `useHeader` hook to extract configuration data such as organizational contact details,
 * logo, and navigation items from the provided `headerData`. It is composed of two main parts: a top bar showing phone
 * and email links, and a menu bar containing the logo and navigation. The component supports test automation through
 * the `testId` prop, which is applied to the root wrapper element as a `data-cy` attribute.
 * It is wrapped with MobX’s `observer` to automatically re-render when observable data changes. It is designed to be
 * responsive and accessible, including ARIA attributes and adaptive logo sizing.
 *
 * @param props            The component props.
 * @param props.headerData The header content and configuration, including contact info, logo, and menu structure.
 * @param props.testId     An optional test ID used for `data-cy` attributes in automated testing. Defaults to `'Header'`.
 * @returns A JSX layout structure for the site’s top header and navigation menu.
 *
 * @example
 * ```tsx
 * <Header headerData={myHeaderData} />
 * ```
 */
const Header = observer(({headerData, testId = 'Header'}: ComponentProps) => {
    const {header, logo} = useHeader(headerData);

    return (
        <Wrapper data-cy={testId} layout layoutRoot layoutScroll>
            <TopBar headerData={header} />
            <MenuBar aria-label="Hauptmenü" as="nav">
                <Link href="/">
                    <LogoImage expectedSizes={{lg: 214, sm: 160, xs: 100}} image={logo} />
                </Link>
                <Menu navigation={header.navigation} testId={`HeaderMenu-${header.id}`} />
            </MenuBar>
        </Wrapper>
    );
});

Header.displayName = 'Header';

export {Header};

const Wrapper = styled(motion.header)`
    background-color: ${({theme}) => theme.colors.backgroundLight};
    flex: 0 0 16rem;
    position: sticky;
    top: 0;
    z-index: 1000;
`;

const MenuBar = styled(Container)`
    align-items: center;
    display: flex;
    justify-content: space-between;
    max-height: 12rem;
    overflow-x: clip;
    padding-block: ${spacing(9)};
    position: relative;
`;

const LogoImage = styled(PayloadImage)`
    height: 55px;
    max-width: 50px;
    object-fit: cover;
    object-position: left center;

    ${media('sm')} {
        height: auto;
        max-width: none;
        object-fit: contain;
        object-position: initial;
        width: 16rem;
    }

    ${media('lg')} {
        height: auto;
        max-width: none;
        object-fit: contain;
        object-position: initial;
        width: 21.4rem;
    }
`;